/**
 * OPS-MASTER-001 — canonical master-data governance registry (extends operational registry architecture).
 * Single registry family: operational modules remain in operationalAccessRegistry.js;
 * master/setup modules live here with governanceCategory = MASTER_DATA.
 */
import { UI_SHELL } from './workflowRoutes.js';

/** @typedef {'OPERATIONAL' | 'MASTER_DATA' | 'GOVERNANCE' | 'LEGACY'} GovernanceCategory */

/** @typedef {'VIEW' | 'CONFIGURE' | 'APPROVE' | 'ADMIN_OVERRIDE'} MasterPermissionLevel */

/**
 * @typedef {object} MasterModuleDef
 * @property {string} id
 * @property {GovernanceCategory} governanceCategory
 * @property {string} masterDomainKey
 * @property {string} setupModule
 * @property {string} configurationScope
 * @property {'healthcare-ui' | 'frontend' | 'dual' | 'api-only'} canonicalOwnerShell
 * @property {string|null} legacyCompatibilitySource
 * @property {string|null} uriId
 * @property {string[]} apiPatterns
 * @property {string[]} routePatterns
 * @property {{ HMS_SHELL?: string, HEALTHCARE_UI?: string }} routes
 * @property {'onboarded' | 'reusable' | 'legacy-only' | 'hidden' | 'duplicate' | 'deprecated' | 'missing' | 'operational-misplaced'} inventoryStatus
 * @property {string} recommendedAction
 * @property {MasterPermissionLevel[]} [supportedLevels]
 * @property {string[]} [permissionKeys]
 * @property {string[]} [compatibilityPermissionKeys] legacy grants during migration (DUP-001)
 * @property {'onboarding-ready' | 'hidden' | 'onboarded' | 'pending'} [governanceOnboardingStatus]
 */

/** Canonical master governance domain keys (separate from OPERATIONS workflow groups). */
export const MASTER_GOVERNANCE_DOMAINS = {
  LABORATORY_MASTERS: 'LABORATORY_MASTERS',
  RADIOLOGY_MASTERS: 'RADIOLOGY_MASTERS',
  PHARMACY_MASTERS: 'PHARMACY_MASTERS',
  BILLING_MASTERS: 'BILLING_MASTERS',
  CLINICAL_MASTERS: 'CLINICAL_MASTERS',
  INVENTORY_MASTERS: 'INVENTORY_MASTERS',
  WORKFORCE_MASTERS: 'WORKFORCE_MASTERS',
  FACILITY_MASTERS: 'FACILITY_MASTERS',
  ENTERPRISE_GOVERNANCE: 'ENTERPRISE_GOVERNANCE',
  IT_ASSET_MASTERS: 'IT_ASSET_MASTERS',
  PLATFORM_ADMIN: 'PLATFORM_ADMIN',
  SYSTEM_ADMINISTRATION: 'SYSTEM_ADMINISTRATION',
};

export const MASTER_PERMISSION_LEVELS = ['VIEW', 'CONFIGURE', 'APPROVE', 'ADMIN_OVERRIDE'];

/** @type {Record<string, MasterModuleDef>} */
export const MASTER_MODULES = {
  // --- Workforce / platform (healthcare-ui onboarded via t_core_menu) ---
  DEPARTMENT_MASTER: {
    id: 'DEPARTMENT_MASTER',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.WORKFORCE_MASTERS,
    setupModule: 'department',
    configurationScope: 'hospital-departments',
    canonicalOwnerShell: 'healthcare-ui',
    legacyCompatibilitySource: null,
    uriId: 'DEPARTMENT',
    permissionKeys: ['DEPARTMENT'],
    apiPatterns: ['/ems/department', '/ems/department/.*'],
    routePatterns: ['/ems/department'],
    routes: { HEALTHCARE_UI: '/ems/department' },
    inventoryStatus: 'onboarded',
    recommendedAction: 'Keep as canonical workforce master; extend under MASTER TABLES → Workforce',
    supportedLevels: ['VIEW', 'CONFIGURE', 'ADMIN_OVERRIDE'],
  },
  EMPLOYEE_ONBOARD_MASTER: {
    id: 'EMPLOYEE_ONBOARD_MASTER',
    governanceCategory: 'GOVERNANCE',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.SYSTEM_ADMINISTRATION,
    setupModule: 'employee-onboard',
    configurationScope: 'workforce-registry',
    canonicalOwnerShell: 'healthcare-ui',
    legacyCompatibilitySource: null,
    uriId: 'EMPLOYEEONBOARD',
    permissionKeys: ['EMPLOYEEONBOARD'],
    apiPatterns: ['/ems/employees/details/.*'],
    routePatterns: ['/employee/onboard'],
    routes: { HEALTHCARE_UI: '/employee/onboard' },
    inventoryStatus: 'onboarded',
    recommendedAction: 'System administration — workforce registry onboarding',
    supportedLevels: ['VIEW', 'CONFIGURE', 'ADMIN_OVERRIDE'],
  },
  DOCTORS_MASTER: {
    id: 'DOCTORS_MASTER',
    governanceCategory: 'GOVERNANCE',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.SYSTEM_ADMINISTRATION,
    setupModule: 'doctors',
    configurationScope: 'medical-professionals',
    canonicalOwnerShell: 'healthcare-ui',
    legacyCompatibilitySource: null,
    uriId: 'DOCTORS',
    permissionKeys: ['DOCTORS'],
    apiPatterns: ['/doctors/onboard/.*'],
    routePatterns: ['/doctors/onboard', '/doctors/search'],
    routes: { HEALTHCARE_UI: '/doctors/onboard' },
    inventoryStatus: 'onboarded',
    recommendedAction: 'System administration — medical professional onboarding',
    supportedLevels: ['VIEW', 'CONFIGURE', 'ADMIN_OVERRIDE'],
  },

  // --- Laboratory masters (frontend-only operational-masters API) ---
  LAB_ENTERPRISE_INVESTIGATIONS: {
    id: 'LAB_ENTERPRISE_INVESTIGATIONS',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.LABORATORY_MASTERS,
    setupModule: 'lab-enterprise-investigations',
    configurationScope: 'enterprise-investigation-catalog',
    canonicalOwnerShell: 'healthcare-ui',
    legacyCompatibilitySource: 'frontend',
    uriId: 'LABMASTERENTERPRISE',
    permissionKeys: ['LABMASTERENTERPRISE'],
    governanceOnboardingStatus: 'onboarding-ready',
    apiPatterns: [
      '/api/v1/lab/operational-masters/enterprise-investigations',
      '/api/v1/lab/operational-masters/enterprise-investigations/.*',
      '/api/v1/lab/ai-catalog',
      '/api/v1/lab/ai-catalog/.*',
    ],
    routePatterns: [
      '/lab/admin/enterprise',
      '/lab/admin/enterprise/*',
      '/lab/admin/catalog/enterprise',
      '/lab/admin/catalog/enterprise/*',
      '/lab/admin/catalog/import',
      '/lab/admin/catalog/import/*',
      '/lab/admin/catalog/conflicts',
      '/lab/admin/catalog/conflicts/*',
      '/lab/admin/catalog/ai-review',
      '/lab/admin/catalog/ai-review/*',
    ],
    routes: {
      HEALTHCARE_UI: '/lab/admin/catalog/enterprise',
      HMS_SHELL: '/lab/admin/catalog/enterprise',
    },
    inventoryStatus: 'onboarded',
    recommendedAction: 'Native Phase-1 catalog console in healthcare-ui; HMS shell remains Phase-2 parity target',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE', 'ADMIN_OVERRIDE'],
  },
  LAB_HOSPITAL_INVESTIGATION_MAPPINGS: {
    id: 'LAB_HOSPITAL_INVESTIGATION_MAPPINGS',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.LABORATORY_MASTERS,
    setupModule: 'lab-hospital-mappings',
    configurationScope: 'hospital-investigation-mapping',
    canonicalOwnerShell: 'healthcare-ui',
    legacyCompatibilitySource: 'frontend',
    uriId: 'LABMASTERHOSPITAL',
    permissionKeys: ['LABMASTERHOSPITAL'],
    governanceOnboardingStatus: 'onboarding-ready',
    apiPatterns: ['/api/v1/lab/operational-masters/hospital-investigations', '/api/v1/lab/operational-masters/hospital-investigations/.*'],
    routePatterns: [
      '/lab/admin/hospital-mappings',
      '/lab/admin/hospital-mappings/*',
      '/lab/admin/catalog/hospital',
      '/lab/admin/catalog/hospital/*',
      '/lab/admin/catalog/tariffs',
      '/lab/admin/catalog/tariffs/*',
      '/lab/admin/catalog/print-settings',
    ],
    routes: {
      HEALTHCARE_UI: '/lab/admin/catalog/hospital',
      HMS_SHELL: '/lab/admin/catalog/hospital',
    },
    inventoryStatus: 'onboarded',
    recommendedAction: 'Native Phase-1 hospital enablement in healthcare-ui',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE'],
  },
  LAB_SETUP_CONSOLE: {
    id: 'LAB_SETUP_CONSOLE',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.LABORATORY_MASTERS,
    setupModule: 'lab-setup',
    configurationScope: 'governed-lab-setup',
    canonicalOwnerShell: 'healthcare-ui',
    legacyCompatibilitySource: 'frontend',
    uriId: 'LABSETUP',
    permissionKeys: ['LABSETUP'],
    governanceOnboardingStatus: 'onboarding-ready',
    apiPatterns: ['/api/v1/master/.*lab.*'],
    routePatterns: ['/setup/lab', '/lab/admin/catalog', '/lab/admin/catalog/*'],
    routes: {
      HEALTHCARE_UI: '/lab/admin/catalog',
      HMS_SHELL: '/setup/lab',
    },
    inventoryStatus: 'onboarded',
    recommendedAction: 'Redirects to native Phase-1 catalog onboarding home in healthcare-ui',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE', 'ADMIN_OVERRIDE'],
  },

  // --- Radiology masters ---
  RAD_ENTERPRISE_SCANS: {
    id: 'RAD_ENTERPRISE_SCANS',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.RADIOLOGY_MASTERS,
    setupModule: 'rad-enterprise-scans',
    configurationScope: 'enterprise-scan-catalog',
    canonicalOwnerShell: 'frontend',
    legacyCompatibilitySource: null,
    uriId: 'RADMASTERENTERPRISE',
    permissionKeys: ['RADMASTERENTERPRISE'],
    governanceOnboardingStatus: 'onboarding-ready',
    apiPatterns: ['/api/v1/radiology/operational-masters/enterprise-scans', '/api/v1/radiology/operational-masters/enterprise-scans/.*'],
    routePatterns: ['/radiology/admin/enterprise'],
    routes: { HMS_SHELL: '/radiology/admin/enterprise' },
    inventoryStatus: 'provisioned',
    recommendedAction: 'Canonical owner frontend — bridge from governance UI when onboarding',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE', 'ADMIN_OVERRIDE'],
  },
  RAD_HOSPITAL_SCAN_MAPPINGS: {
    id: 'RAD_HOSPITAL_SCAN_MAPPINGS',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.RADIOLOGY_MASTERS,
    setupModule: 'rad-hospital-mappings',
    configurationScope: 'hospital-scan-mapping',
    canonicalOwnerShell: 'frontend',
    legacyCompatibilitySource: null,
    uriId: 'RADMASTERHOSPITAL',
    permissionKeys: ['RADMASTERHOSPITAL'],
    governanceOnboardingStatus: 'onboarding-ready',
    apiPatterns: ['/api/v1/radiology/operational-masters/hospital-scans', '/api/v1/radiology/operational-masters/hospital-scans/.*'],
    routePatterns: ['/radiology/admin/hospital-mappings'],
    routes: { HMS_SHELL: '/radiology/admin/hospital-mappings' },
    inventoryStatus: 'provisioned',
    recommendedAction: 'Pair with enterprise scans under Radiology masters domain',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE'],
  },

  // --- Pharmacy masters ---
  PHARMACY_ENTERPRISE_MEDICINES: {
    id: 'PHARMACY_ENTERPRISE_MEDICINES',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.PHARMACY_MASTERS,
    setupModule: 'pharmacy-enterprise-medicines',
    configurationScope: 'enterprise-medicine-catalog',
    canonicalOwnerShell: 'frontend',
    legacyCompatibilitySource: null,
    uriId: 'PHARMACYMASTERENTERPRISE',
    permissionKeys: [
      'PHARMACYMASTERENTERPRISE',
      'PHARMACYMASTERENTERPRISE:BULKIMPORT',
      'PHARMACYMASTERENTERPRISE:IMPORT',
      'PHARMACYMASTERENTERPRISE:IMPORTSESSION:API',
      'PHARMACYMASTERENTERPRISE:IMPORTREPORT',
      'DOCUMENTAI',
      'DOCUMENTAI:API',
    ],
    governanceOnboardingStatus: 'onboarding-ready',
    apiPatterns: [
      '/api/v1/pharmacy/operational-masters/enterprise-medicines',
      '/api/v1/pharmacy/operational-masters/enterprise-medicines/.*',
      '/api/v1/pharmacy/operational-masters/hospital-medicines',
      '/api/v1/pharmacy/operational-masters/hospital-medicines/.*',
      '/api/v1/pharmacy/operational-masters/import-sessions',
      '/api/v1/pharmacy/operational-masters/import-sessions/.*',
      '/api/v1/ai/document/extract',
      '/api/v1/ai/document/.*',
      '/api/v1/pharmacy/masters/categories',
      '/api/v1/pharmacy/masters/categories/.*',
    ],
    routePatterns: [
      '/pharmacy/admin/enterprise',
      '/pharmacy/admin/enterprise/import',
      '/pharmacy/admin/enterprise/import/reports/*',
      '/pharmacy/admin/enterprise/duplicates',
      '/pharmacy/admin/enterprise/ai-review',
      '/pharmacy/admin/hospital-mapping',
      '/pharmacy/admin/hospital-pricing',
      '/pharmacy/admin/categories',
      // Shared Pharmacy Setup Overview entry (does not grant Configuration)
      '/pharmacy/admin/setup',
    ],
    routes: {
      HMS_SHELL: '/pharmacy/admin/enterprise',
      HEALTHCARE_UI: '/pharmacy/admin/enterprise',
    },
    inventoryStatus: 'provisioned',
    recommendedAction:
      'Catalog + import/report/duplicates/AI tabs under Pharmacy Setup; Overview at /pharmacy/admin/setup; Configuration remains PHARMACYSETUP',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE', 'ADMIN_OVERRIDE'],
  },
  PHARMACY_HOSPITAL_INVENTORY_MAPPING: {
    id: 'PHARMACY_HOSPITAL_INVENTORY_MAPPING',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.INVENTORY_MASTERS,
    setupModule: 'pharmacy-hospital-inventory',
    configurationScope: 'hospital-medicine-inventory',
    canonicalOwnerShell: 'frontend',
    legacyCompatibilitySource: null,
    uriId: 'PHARMACYMASTERHOSPITAL',
    permissionKeys: ['PHARMACYMASTERHOSPITAL'],
    governanceOnboardingStatus: 'onboarding-ready',
    apiPatterns: [
      '/api/v1/pharmacy/operational-masters/hospital-medicines',
      '/api/v1/pharmacy/operational-masters/hospital-medicines/.*',
      '/api/v1/pharmacy/masters/categories',
      '/api/v1/pharmacy/masters/categories/.*',
    ],
    routePatterns: [
      '/pharmacy/admin/hospital-mapping',
      '/pharmacy/admin/hospital-pricing',
      '/pharmacy/admin/categories',
      '/pharmacy/admin/setup',
    ],
    routes: {
      HMS_SHELL: '/pharmacy/admin/hospital-mapping',
      HEALTHCARE_UI: '/pharmacy/admin/hospital-mapping',
    },
    inventoryStatus: 'provisioned',
    recommendedAction: 'Hospital mapping/pricing tabs inside Pharmacy Setup — not operational PHARMACY_COUNTER',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE'],
  },
  PHARMACY_SETUP_GENERICS: {
    id: 'PHARMACY_SETUP_GENERICS',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.PHARMACY_MASTERS,
    setupModule: 'pharmacy-generics',
    configurationScope: 'governed-generics',
    canonicalOwnerShell: 'frontend',
    legacyCompatibilitySource: 'master-data-service',
    uriId: 'PHARMACYSETUP',
    permissionKeys: ['PHARMACYSETUP'],
    governanceOnboardingStatus: 'onboarding-ready',
    apiPatterns: ['/api/v1/master/pharmacy/generics', '/api/v1/master/pharmacy/.*'],
    routePatterns: [
      '/pharmacy/admin/setup',
      '/setup/pharmacy',
      '/setup/pharmacy/configuration',
      '/setup/pharmacy/configuration/*',
    ],
    routes: {
      HMS_SHELL: '/setup/pharmacy',
      HEALTHCARE_UI: '/setup/pharmacy',
    },
    inventoryStatus: 'provisioned',
    recommendedAction:
      'Pharmacy Setup Overview + Configuration (generics/brands/reference manufacturers) inside workspace',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE', 'ADMIN_OVERRIDE'],
  },
  PHARMACY_STOCK_OPS: {
    id: 'PHARMACY_STOCK_OPS',
    governanceCategory: 'OPERATIONAL',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.INVENTORY_MASTERS,
    setupModule: 'pharmacy-stock',
    configurationScope: 'stock-batches-ops',
    canonicalOwnerShell: 'dual',
    legacyCompatibilitySource: null,
    uriId: 'PHARMACYCOUNTER',
    apiPatterns: ['/pharmacy/stock', '/pharmacy/batches'],
    routePatterns: ['/pharmacy/stock', '/pharmacy/batches'],
    routes: { HMS_SHELL: '/pharmacy/stock', HEALTHCARE_UI: '/pharmacy/counter' },
    inventoryStatus: 'operational-misplaced',
    recommendedAction: 'Keep under OPERATIONS — not master governance onboarding',
    supportedLevels: ['VIEW', 'CONFIGURE'],
  },

  // --- Billing masters ---
  BILLING_TARIFF_SETUP: {
    id: 'BILLING_TARIFF_SETUP',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.BILLING_MASTERS,
    setupModule: 'billing-tariffs',
    configurationScope: 'tariff-versions',
    canonicalOwnerShell: 'frontend',
    legacyCompatibilitySource: 'master-data-service',
    uriId: 'BILLINGMASTERTARIFF',
    permissionKeys: ['BILLINGMASTERTARIFF'],
    governanceOnboardingStatus: 'onboarding-ready',
    apiPatterns: [
      '/api/v1/master/billing/tariff-versions',
      '/api/v1/master/billing/.*',
      '/api/v1/billing/invoice-print-settings',
    ],
    routePatterns: ['/setup/billing', '/setup/billing/print-settings'],
    routes: { HMS_SHELL: '/setup/billing', HEALTHCARE_UI: '/setup/billing' },
    inventoryStatus: 'provisioned',
    recommendedAction: 'Canonical billing tariff master — separate from service catalog',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE', 'ADMIN_OVERRIDE'],
  },
  BILLING_SERVICE_CATALOG: {
    id: 'BILLING_SERVICE_CATALOG',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.BILLING_MASTERS,
    setupModule: 'service-catalog',
    configurationScope: 'chargeable-services',
    canonicalOwnerShell: 'frontend',
    legacyCompatibilitySource: null,
    uriId: 'BILLINGSERVICECATALOG',
    permissionKeys: ['BILLINGSERVICECATALOG'],
    compatibilityPermissionKeys: ['BILLINGWORKSPACE', 'BILLING_WORKSPACE'],
    governanceOnboardingStatus: 'onboarding-ready',
    apiPatterns: ['/api/v1/billing/catalog/.*'],
    routePatterns: ['/billing/catalog'],
    routes: { HMS_SHELL: '/billing/catalog', HEALTHCARE_UI: '/billing/catalog' },
    inventoryStatus: 'provisioned',
    recommendedAction: 'DUP-001 resolved — canonical master uri; counter remains FONE:BILLINGWORKSPACE / OPBILLINGCOUNTER',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE', 'ADMIN_OVERRIDE'],
  },
  BILLING_OPERATIONAL_MASTER_CONSOLE: {
    id: 'BILLING_OPERATIONAL_MASTER_CONSOLE',
    governanceCategory: 'GOVERNANCE',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.ENTERPRISE_GOVERNANCE,
    setupModule: 'billing-operational-master',
    configurationScope: 'runtime-governance-metadata',
    canonicalOwnerShell: 'frontend',
    legacyCompatibilitySource: null,
    uriId: 'OPERATIONALALERTS',
    apiPatterns: ['/api/v1/billing/operational-master/.*'],
    routePatterns: ['/billing/operational-master', '/billing/operational-*'],
    routes: { HMS_SHELL: '/billing/operational-master' },
    inventoryStatus: 'operational-misplaced',
    recommendedAction: 'NOT master-data — keep under operational governance; exclude from MASTER TABLES',
    supportedLevels: ['VIEW', 'CONFIGURE', 'ADMIN_OVERRIDE'],
  },
  BILLING_PAYMENT_GATEWAY: {
    id: 'BILLING_PAYMENT_GATEWAY',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.BILLING_MASTERS,
    setupModule: 'payment-gateway',
    configurationScope: 'payment-rail-config',
    canonicalOwnerShell: 'healthcare-ui',
    legacyCompatibilitySource: null,
    uriId: null,
    apiPatterns: ['/billing/payment-gateway'],
    routePatterns: ['/billing/payment-gateway'],
    routes: { HEALTHCARE_UI: '/billing/payment-gateway' },
    inventoryStatus: 'reusable',
    recommendedAction: 'Onboard under Billing masters after uriId provisioning',
    supportedLevels: ['VIEW', 'CONFIGURE', 'ADMIN_OVERRIDE'],
  },

  // --- Clinical masters ---
  CLINICAL_SETUP_PLACEHOLDER: {
    id: 'CLINICAL_SETUP_PLACEHOLDER',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.CLINICAL_MASTERS,
    setupModule: 'clinical-masters',
    configurationScope: 'clinical-reference-data',
    canonicalOwnerShell: 'frontend',
    legacyCompatibilitySource: null,
    uriId: 'CLINICALSETUP',
    permissionKeys: ['CLINICALSETUP'],
    governanceOnboardingStatus: 'onboarding-ready',
    apiPatterns: ['/api/v1/master/.*clinical.*'],
    routePatterns: ['/setup/clinical'],
    routes: { HMS_SHELL: '/setup/clinical' },
    inventoryStatus: 'provisioned',
    recommendedAction: 'Build when master-data clinical controllers exist — no duplicate OPD consult',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE'],
  },

  // --- Facility / enterprise setup (master-data-service) ---
  FACILITY_SETUP: {
    id: 'FACILITY_SETUP',
    governanceCategory: 'MASTER_DATA',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.FACILITY_MASTERS,
    setupModule: 'facility',
    configurationScope: 'facility-topology',
    canonicalOwnerShell: 'frontend',
    legacyCompatibilitySource: 'master-data-service',
    uriId: 'FACILITYSETUP',
    permissionKeys: ['FACILITYSETUP'],
    governanceOnboardingStatus: 'onboarding-ready',
    apiPatterns: ['/api/v1/master/facility', '/api/v1/master/facility/.*'],
    routePatterns: ['/setup/facility'],
    routes: { HMS_SHELL: '/setup/facility' },
    inventoryStatus: 'provisioned',
    recommendedAction: 'Canonical facility master — onboard under Facility masters group',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE', 'ADMIN_OVERRIDE'],
  },
  MASTER_PUBLISH_CONSOLE: {
    id: 'MASTER_PUBLISH_CONSOLE',
    governanceCategory: 'GOVERNANCE',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.ENTERPRISE_GOVERNANCE,
    setupModule: 'publish',
    configurationScope: 'master-publish-projections',
    canonicalOwnerShell: 'frontend',
    legacyCompatibilitySource: null,
    uriId: 'MASTERPUBLISH',
    permissionKeys: ['MASTERPUBLISH'],
    governanceOnboardingStatus: 'hidden',
    apiPatterns: ['/api/v1/master/publish', '/api/v1/master/publish/.*'],
    routePatterns: ['/setup/publish', '/setup/publish/*'],
    routes: { HMS_SHELL: '/setup/publish' },
    inventoryStatus: 'reusable',
    recommendedAction: 'Governance category GOVERNANCE — not clinical/lab master',
    supportedLevels: ['VIEW', 'CONFIGURE', 'APPROVE', 'ADMIN_OVERRIDE'],
  },

  // --- IT asset inventory (legacy ITCMS — not hospital inventory) ---
  IT_ASSET_CATEGORY: {
    id: 'IT_ASSET_CATEGORY',
    governanceCategory: 'LEGACY',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.IT_ASSET_MASTERS,
    setupModule: 'asset-category',
    configurationScope: 'itcms-asset-reference',
    canonicalOwnerShell: 'healthcare-ui',
    legacyCompatibilitySource: 'ITCMS',
    uriId: 'ASSETCATEGORY',
    apiPatterns: ['/inv/asset-category', '/asset/category'],
    routePatterns: ['/asset/category', '/inv/asset-type'],
    routes: { HEALTHCARE_UI: '/asset/category' },
    inventoryStatus: 'legacy-only',
    recommendedAction: 'Deprioritize for hospital master onboarding — separate IT vs clinical inventory',
    supportedLevels: ['VIEW', 'CONFIGURE'],
  },

  // --- System administration (platform RBAC — never master-table migration) ---
  ACCESS_CONTROL_GOVERNANCE: {
    id: 'ACCESS_CONTROL_GOVERNANCE',
    governanceCategory: 'GOVERNANCE',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.SYSTEM_ADMINISTRATION,
    setupModule: 'access-control',
    configurationScope: 'rbac-governance',
    canonicalOwnerShell: 'healthcare-ui',
    legacyCompatibilitySource: null,
    uriId: 'ACCESSCONTROL',
    permissionKeys: ['ACCESSCONTROL'],
    apiPatterns: ['/admin/access-control/.*'],
    routePatterns: ['/access/control'],
    routes: { HEALTHCARE_UI: '/access/control' },
    inventoryStatus: 'onboarded',
    recommendedAction: 'Permission Matrix — access control governance console',
    supportedLevels: ['VIEW', 'CONFIGURE', 'ADMIN_OVERRIDE'],
  },
  ROLE_MANAGEMENT_GOVERNANCE: {
    id: 'ROLE_MANAGEMENT_GOVERNANCE',
    governanceCategory: 'GOVERNANCE',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.SYSTEM_ADMINISTRATION,
    setupModule: 'manage-roles',
    configurationScope: 'role-catalog',
    canonicalOwnerShell: 'healthcare-ui',
    legacyCompatibilitySource: null,
    uriId: 'MANAGEROLES',
    permissionKeys: ['MANAGEROLES'],
    apiPatterns: ['/admin/roles/.*'],
    routePatterns: ['/roles/manage-roles'],
    routes: { HEALTHCARE_UI: '/roles/manage-roles' },
    inventoryStatus: 'onboarded',
    recommendedAction: 'Role Management CRUD',
    supportedLevels: ['VIEW', 'CONFIGURE', 'ADMIN_OVERRIDE'],
  },
  USER_ROLE_ASSIGNMENT_GOVERNANCE: {
    id: 'USER_ROLE_ASSIGNMENT_GOVERNANCE',
    governanceCategory: 'GOVERNANCE',
    masterDomainKey: MASTER_GOVERNANCE_DOMAINS.SYSTEM_ADMINISTRATION,
    setupModule: 'manage-user-roles',
    configurationScope: 'module-user-assignment',
    canonicalOwnerShell: 'healthcare-ui',
    legacyCompatibilitySource: 'MANAGEUSERS',
    uriId: 'MANAGEUSERROLES',
    permissionKeys: ['MANAGEUSERROLES'],
    compatibilityPermissionKeys: ['MANAGEUSERS'],
    apiPatterns: ['/admin/userRole/.*'],
    routePatterns: ['/users/manage-roles', '/users/manage-user-roles'],
    routes: { HEALTHCARE_UI: '/users/manage-roles' },
    inventoryStatus: 'onboarded',
    recommendedAction: 'User Role Assignment and Module Administration share FONE:MANAGEUSERROLES',
    supportedLevels: ['VIEW', 'CONFIGURE', 'ADMIN_OVERRIDE'],
  },
};

export const MASTER_MODULE_IDS = Object.keys(MASTER_MODULES);

export const MASTER_DOMAIN_ORDER = [
  MASTER_GOVERNANCE_DOMAINS.LABORATORY_MASTERS,
  MASTER_GOVERNANCE_DOMAINS.RADIOLOGY_MASTERS,
  MASTER_GOVERNANCE_DOMAINS.PHARMACY_MASTERS,
  MASTER_GOVERNANCE_DOMAINS.BILLING_MASTERS,
  MASTER_GOVERNANCE_DOMAINS.CLINICAL_MASTERS,
  MASTER_GOVERNANCE_DOMAINS.INVENTORY_MASTERS,
  MASTER_GOVERNANCE_DOMAINS.WORKFORCE_MASTERS,
  MASTER_GOVERNANCE_DOMAINS.FACILITY_MASTERS,
  MASTER_GOVERNANCE_DOMAINS.ENTERPRISE_GOVERNANCE,
  MASTER_GOVERNANCE_DOMAINS.IT_ASSET_MASTERS,
  MASTER_GOVERNANCE_DOMAINS.PLATFORM_ADMIN,
  MASTER_GOVERNANCE_DOMAINS.SYSTEM_ADMINISTRATION,
];

export function masterModulesByDomain(domainKey) {
  return MASTER_MODULE_IDS.filter((id) => MASTER_MODULES[id].masterDomainKey === domainKey);
}

export function masterRoute(moduleId, shell = UI_SHELL.HEALTHCARE_UI) {
  return MASTER_MODULES[moduleId]?.routes?.[shell] ?? null;
}

/** Canonical uriId → master module (MASTER_DATA only). Matches by uri suffix across tenant prefixes. */
export function masterModuleByUriId(uriId) {
  if (!uriId) return null;
  const colon = String(uriId).trim().indexOf(':');
  const wantedSuffix = (colon === -1 ? uriId : uriId.slice(colon + 1)).trim().toUpperCase();
  if (!wantedSuffix) return null;
  for (const id of MASTER_MODULE_IDS) {
    const mod = MASTER_MODULES[id];
    if (mod.governanceCategory !== 'MASTER_DATA' || !mod.uriId) continue;
    const modColon = mod.uriId.indexOf(':');
    const modSuffix = (modColon === -1 ? mod.uriId : mod.uriId.slice(modColon + 1)).trim().toUpperCase();
    if (modSuffix === wantedSuffix) return mod;
  }
  return null;
}

/** OPS-MASTER-002 — ownership matrix row per canonical uriId. */
export function buildCanonicalOwnershipMatrix() {
  return MASTER_MODULE_IDS.filter((id) => MASTER_MODULES[id].uriId).map((id) => {
    const mod = MASTER_MODULES[id];
    return {
      moduleId: id,
      uriId: mod.uriId,
      governanceCategory: mod.governanceCategory,
      masterDomainKey: mod.masterDomainKey,
      canonicalOwnerShell: mod.canonicalOwnerShell,
      runtimeOwnership: mod.governanceCategory === 'MASTER_DATA' ? 'setup' : mod.governanceCategory.toLowerCase(),
    };
  });
}
